import { Button, Modal } from "antd";
import { Location } from "history";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { setEnv, setPendingEnv } from "redux/reducers/GlobalUiSlice";
import { Prompt } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";

interface CustomPromptProps {
  when?: boolean | undefined;
  navigate: (path: string) => void;
  shouldBlockNavigation: (location: Location) => boolean;
  onSaveChanges: () => Promise<void>;
}

const CustomPrompt = ({
  when,
  navigate,
  shouldBlockNavigation,
  onSaveChanges
}: CustomPromptProps): JSX.Element => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [lastLocation, setLastLocation] = useState<Location | null>(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  useEffect(() => {
    window.onbeforeunload = when ? () => true : null;
    return () => {
      window.onbeforeunload = null;
    };
  }, [when]);

  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      navigate(lastLocation.pathname);
    }
  }, [confirmedNavigation, lastLocation]);

  const closeModal = () => {
    setModalVisible(false);
  };

  const handleBlockedNavigation = (nextLocation: Location): boolean => {
    if (!confirmedNavigation && shouldBlockNavigation(nextLocation)) {
      setModalVisible(true);
      setLastLocation(nextLocation);
      return false;
    }
    return true;
  };

  const handleConfirmNavigationClick = useCallback(() => {
    setModalVisible(false);
    setConfirmedNavigation(true);
  }, []);

  const handleSaveChanges = useCallback(async () => {
    await onSaveChanges?.();
    setModalVisible(false);
    handleConfirmNavigationClick();
  }, [onSaveChanges, handleConfirmNavigationClick]);

  return (
    <>
      <Prompt when={when} message={handleBlockedNavigation} />
      <Modal
        open={modalVisible}
        title={t("prompt.title")}
        closable={true}
        onCancel={closeModal}
        footer={[
          <div>
            <Button onClick={closeModal}>{t("prompt.stayBtn")}</Button>
            <Button onClick={handleConfirmNavigationClick}>{t("prompt.leaveBtn")}</Button>
            <Button type="primary" onClick={handleSaveChanges}>
              {t("prompt.saveBtn")}
            </Button>
          </div>
        ]}
      >
        {t("prompt.desc")}
      </Modal>
    </>
  );
};
export default CustomPrompt;
